import { Chip } from '@mui/material';

/**
 * Status pill for an Issue. Strips the `status:` prefix and renders
 * the remainder title-cased. Colour mapping follows the pipeline
 * stages from raised → completed; status:planned is loudest (amber)
 * because it's the one human-action-required stage.
 */
export function IssueStatusChip({ label }: { label: string }) {
  const status = label.startsWith('status:') ? label.slice('status:'.length) : label;
  const display = titleCase(status);
  const color = colourForStatus(status);
  if (color === 'amber') {
    return <Chip size="small" label={display}
                 sx={{ bgcolor: '#f59e0b', color: '#0a0a0a', fontWeight: 600 }} />;
  }
  if (color === 'violet') {
    // in-review: developed, PR open — a human-action state, so render it loud.
    return <Chip size="small" label={display}
                 sx={{ bgcolor: '#7c3aed', color: '#ffffff', fontWeight: 600 }} />;
  }
  return <Chip size="small" label={display} color={color} />;
}

type MuiColour = 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'info';

function colourForStatus(status: string): MuiColour | 'amber' | 'violet' {
  switch (status) {
    case 'raised':                   return 'warning';
    case 'verified':                 return 'info';
    case 'approved-for-development': return 'primary';
    case 'planned':                  return 'amber';
    case 'ready-for-development':    return 'secondary';
    case 'in-review':                return 'violet';
    case 'completed':                return 'success';
    default:                         return 'default';
  }
}

function titleCase(s: string): string {
  return s.split('-')
    .map(w => w.length === 0 ? '' : w[0].toUpperCase() + w.slice(1))
    .join(' ');
}

/** Helper used in lists / charts — extract the issue's status label. */
export function statusFromLabels(labels: string[]): string | undefined {
  return labels.find(l => l.startsWith('status:'));
}

/** Categorical label helpers — mirror the labels seeded in setup-labels.sh. */
export function categoryFromLabels(labels: string[]): string | undefined {
  const l = labels.find(x => x.startsWith('cat:'));
  return l?.slice('cat:'.length);
}

export function priorityFromLabels(labels: string[]): string | undefined {
  const l = labels.find(x => x.startsWith('priority:'));
  return l?.slice('priority:'.length);
}
